import { useTranslation } from 'react-i18next';

import isEmpty from 'lodash/isEmpty';

import { resolveStorageClassDisplayLabel } from '../../../components/storageClassField';
import { VolumeResourceCard, type VolumeResourceExtraItem } from '../../../components/volumeResourceCard';
import { usePvcOptions } from '../../shared-volume/hooks/usePvcOptions';
import styles from '../index.module.less';
import { buildMountedVolumeCapacityMap } from '../utils';
import {
  type VolumeClaimTemplateItem,
  type VolumeItem,
  VolumeMountTab,
  VolumeMountType,
  type VolumeMountsProps,
  type VolumeStorageItem,
  isVolumeClaimTemplateItem,
  isVolumeStorageItem,
} from '../volumeTypes';

interface VolumeItemAction {
  onEdit?: (item: VolumeItem, index: number) => void;
  onDelete?: (item: VolumeItem) => void;
}

interface StorageItemsProps extends VolumeItemAction {
  items: VolumeItem[];
  mode?: string;
  storageClassErrorIndexes?: Set<number>;
  /** optionMeta 解析的存储类展示名：key = storageClass value */
  storageClassLabelMap?: Record<string, string>;
  canEdit?: boolean;
  canDelete?: boolean;
  namespace?: string;
  /** 是否为挂载的已有 PVC 查询并展示容量。 */
  showMountedVolumeCapacity?: boolean;
}

/** 容器存储列表：存储信息、挂载路径、访问模式同属一张 VolumeResourceCard */
export function StorageItems({
  items,
  mode,
  onEdit,
  onDelete,
  storageClassErrorIndexes,
  storageClassLabelMap,
  canEdit = true,
  canDelete = true,
  namespace,
  showMountedVolumeCapacity = false,
}: StorageItemsProps) {
  const { t } = useTranslation();
  const unconfiguredLabel = t('data-center:maas.storage.not.configured');
  const hasMountedVolume = Array.isArray(items) && items.some(isVolumeStorageItem);
  const { options: pvcOptions } = usePvcOptions(namespace, showMountedVolumeCapacity && hasMountedVolume && Boolean(namespace));
  const mountedVolumeCapacityMap = buildMountedVolumeCapacityMap(pvcOptions);
  if (isEmpty(items) || !Array.isArray(items)) return null;

  const interactive = mode !== 'readonly';

  return (
    <div className={styles.volumeList}>
      {items.map((item, index) => {
        if (isVolumeClaimTemplateItem(item)) {
          return (
            <VolumeClaimTemplateCard
              key={`${item.volumeName ?? 'claim'}-${index}`}
              t={t}
              index={index}
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
              storageClassError={storageClassErrorIndexes?.has(index)}
              unconfiguredLabel={unconfiguredLabel}
              storageClassLabelMap={storageClassLabelMap}
              canEdit={interactive && canEdit}
              canDelete={interactive && canDelete}
            />
          );
        }
        if (isVolumeStorageItem(item)) {
          return (
            <VolumeMountCard
              key={`${item.volumeName ?? 'mount'}-${index}`}
              t={t}
              index={index}
              item={item}
              onEdit={onEdit}
              onDelete={onDelete}
              canEdit={interactive && canEdit}
              canDelete={interactive && canDelete}
              capacity={
                showMountedVolumeCapacity
                  ? (mountedVolumeCapacityMap.get(item.selected) ?? (item.volumeCapacity ? `${item.volumeCapacity}Gi` : undefined))
                  : undefined
              }
            />
          );
        }
        return null;
      })}
    </div>
  );
}

interface VolumeClaimTemplateCardProps extends VolumeItemAction {
  t: (key: string) => string;
  item: VolumeClaimTemplateItem;
  index: number;
  storageClassError?: boolean;
  unconfiguredLabel: string;
  storageClassLabelMap?: Record<string, string>;
  canEdit?: boolean;
  canDelete?: boolean;
}

function VolumeClaimTemplateCard({
  t,
  item,
  index,
  onEdit,
  onDelete,
  storageClassError,
  unconfiguredLabel,
  storageClassLabelMap,
  canEdit = true,
  canDelete = true,
}: VolumeClaimTemplateCardProps) {
  const storageClassValue = item.storageClass?.trim();
  const storageClassLabel = resolveStorageClassDisplayLabel(storageClassValue, unconfiguredLabel, {
    displayName: storageClassValue ? storageClassLabelMap?.[storageClassValue] : undefined,
  });
  const mountPath = readPrimaryMountPath(item.volumeMounts);
  const accessModeLabel = formatAccessModes(item.accessModes);
  const extraItems: VolumeResourceExtraItem[] = [
    { label: t('common:capacity'), value: item.volumeCapacity ? `${item.volumeCapacity}Gi` : '-' },
    { label: t('common:mount.path'), value: mountPath || '-' },
    { label: t('common:access.mode'), value: accessModeLabel || '-' },
    { label: t('common:reclaim.policy'), value: item.reclaimPolicy || '-' },
  ];

  return (
    <VolumeResourceCard
      value={item.volumeName ?? ''}
      label={`${t('common:storage.class')}: ${storageClassLabel}`}
      iconfontName="icon-chijiujuanshengming"
      error={storageClassError}
      // 各卷使用相同字段列宽，避免内容长度和访问模式数量改变列位置。
      labelStyle={{
        display: 'grid',
        alignItems: 'start',
        gridTemplateColumns: `36px minmax(0, 2fr) repeat(${extraItems.length}, minmax(110px, 1fr))`,
      }}
      extraItems={extraItems}
      canEdit={canEdit}
      canDelete={canDelete}
      onEdit={() => onEdit?.(item, index)}
      onDelete={() => onDelete?.(item)}
    />
  );
}

interface VolumeMountCardProps extends VolumeItemAction {
  t: (key: string) => string;
  item: VolumeStorageItem;
  index: number;
  canEdit?: boolean;
  canDelete?: boolean;
  capacity?: string;
}

function VolumeMountCard({ t, item, onEdit, onDelete, index, canEdit = true, canDelete = true, capacity }: VolumeMountCardProps) {
  const mountPath = readPrimaryMountPath(item.volumeMounts, { requireMounted: true });
  const accessModeLabel = formatMountTypeLabel(t, item.volumeMounts?.find((m) => m.mountType !== VolumeMountType.NotMount)?.mountType);
  const extraItems: VolumeResourceExtraItem[] = [
    { label: t('common:capacity'), value: capacity || '-' },
    ...(item.type === VolumeMountTab.HOST_PATH ? [{ value: item.volumeHostPath, label: 'HostPath' }] : []),
    { label: t('common:mount.path'), value: mountPath || '-' },
    { label: t('common:access.mode'), value: accessModeLabel || '-' },
    { label: t('common:reclaim.policy'), value: item.reclaimPolicy || '-' },
  ];

  return (
    <VolumeResourceCard
      value={item.volumeName ?? ''}
      label={t('common:volume.type')}
      iconfontName="icon-guazaizilujing"
      // 各卷使用相同字段列宽，避免内容长度和访问模式数量改变列位置。
      labelStyle={{
        display: 'grid',
        alignItems: 'start',
        gridTemplateColumns: `36px minmax(0, 2fr) repeat(${extraItems.length}, minmax(110px, 1fr))`,
      }}
      extraItems={extraItems}
      canEdit={canEdit}
      canDelete={canDelete}
      onEdit={() => onEdit?.(item, index)}
      onDelete={() => onDelete?.(item)}
    />
  );
}

function readPrimaryMountPath(items: VolumeMountsProps['volumeMounts'], options?: { requireMounted?: boolean }): string {
  const mount = items?.find((item) => {
    if (!item.mountPath?.trim()) return false;
    if (options?.requireMounted) return item.mountType !== VolumeMountType.NotMount;
    return true;
  });
  return mount?.mountPath?.trim() ?? '';
}

function formatAccessModes(accessModes?: string[]) {
  // 每个访问模式独占一行，保留完整名称，避免按字符折行。
  return accessModes?.length
    ? accessModes.filter(Boolean).map((mode) => (
        <span key={mode} style={{ display: 'block', whiteSpace: 'nowrap' }}>
          {mode}
        </span>
      ))
    : '';
}

function formatMountTypeLabel(t: (key: string) => string, mountType: VolumeMountType | string | undefined): string {
  if (!mountType || mountType === VolumeMountType.NotMount) return '';
  if (mountType === VolumeMountType.ReadOnly) return t('common:read.only');
  if (mountType === VolumeMountType.ReadWrite) return t('common:read.write');
  return String(mountType);
}
